import { settingsAPI } from '../services/api';
import createTenantCache from '../services/createTenantCache';
import { subscribe as subscribeToSettingsCache, COMPANY_PROFILE_SIGNAL } from '../services/settingsListCache';

/**
 * useCompanyProfile — fetch + cache the tenant's company profile.
 *
 * Used by AppBar to show the company name on every page (LAT-05), and by the
 * Review & Send / Sent views for the document letterhead. Cached at module
 * level so multiple components don't refetch; revalidated when `refresh()` is
 * called (e.g. after the user saves Settings -> Company).
 *
 * IDI-304: the cache is scoped to the signed-in tenant (services/tenantScope)
 * and cleared on logout, so one dealer's company name can no longer render
 * for the next dealer who signs in on the same tab.
 */
const cache = createTenantCache(() => settingsAPI.getCompanyProfile(), { label: 'useCompanyProfile' });

export function refreshCompanyProfile() {
  return cache.refresh();
}

// IDI-718: a save in Settings re-reads this copy (its own key only; the
// "everything" signal is covered by the tenant reset). Until the answer
// arrives, and if the re-read fails, the old value stays on screen.
subscribeToSettingsCache((key) => {
  if (key === COMPANY_PROFILE_SIGNAL) refreshCompanyProfile();
});

/** Test-only escape hatch. */
export function __resetCompanyProfileCache() {
  cache.reset();
  cache.__clearSubscribers();
}

export default function useCompanyProfile() {
  const { value: profile, loading } = cache.useCached();
  return {
    profile: profile || {},
    companyName: profile?.company_name || '',
    loading,
    refresh: refreshCompanyProfile,
  };
}
